import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import * as Sentry from '@sentry/angular';
import { AppError, WINDOW } from '@hglobal/common';
import { environment } from '@pt/environment';

@Injectable({
  providedIn: 'root',
})
export class SentryErrorHandler implements ErrorHandler {
  readonly #window = inject(WINDOW);

  handleError(error: AppError): void {
    if (this.#isSentryError(error)) {
      return;
    }

    this.sendErrorToSentry(error);

    throw error;
  }

  sendErrorToSentry(error: AppError): void {
    if (!environment.sentry?.dsn) {
      return;
    }

    if (error?.originalError instanceof HttpErrorResponse) {
      const httpError = new Error();
      httpError.name = `${error.originalError.status}: ${error.originalError.url}`;
      httpError.message = `${error.code} on ${this.#window.location.href}`;

      Sentry.captureException(httpError, scope => {
        scope.setTransactionName(httpError.message);
        scope.setLevel('error');
        scope.setExtras({
          originalError: error.originalError,
          code: error.code,
          data: error.data,
          message: error.message,
        });

        scope.addBreadcrumb({
          message: httpError.message,
          level: 'error',
          data: {
            status: error.originalError.status,
            url: error.originalError.url,
            code: error.code,
            data: error.data,
          },
        });
        return scope;
      });
    } else {
      Sentry.captureException(error.originalError || error, scope => {
        scope.setLevel('error');
        if (error.code) {
          scope.setTag('error.code', error.code);
        }
        if (error.data) {
          scope.setExtras({ data: error.data });
        }
        return scope;
      });
    }
  }

  /**
   * Skip errors from sentry.io domains.
   * Do not show it in console
   */
  #isSentryError(error: AppError): boolean {
    if (error?.originalError instanceof HttpErrorResponse) {
      const url = error.originalError.url;
      return url?.includes('sentry.io') || false;
    }

    return false;
  }
}
